import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout/legacy';
import { createUseStyles } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import styles from './styles';

const useStyles = createUseStyles(styles, 'GridLayout');

const ResponsiveGridLayout = WidthProvider(Responsive);

const ROW_HEIGHT = 24;
const MARGIN = 16;

const GridLayout = memo(
    ({
        widgets,
        layouts,
        layout,
        onResize,
        onResizeStop,
        onDrag,
        onDragStop,
        onLayoutChange,
        selected,
        canDrag = false,
        canResize = false,
        adaptativeHeight = false,
    }) => {
        const gridRef = useRef();
        const [gridHeight, setGridHeight] = useState();
        const classes = useStyles();

        useEffect(() => {
            const gridObserver = new ResizeObserver(() => {
                setGridHeight(gridRef.current.offsetHeight);
            });
            gridObserver.observe(gridRef.current);
            return () => gridObserver.disconnect();
        }, [adaptativeHeight]);

        const baseLayout = useMemo(
            () => ({
                isBound: true,
                isDraggable: canDrag,
                isResizable: canResize,
            }),
            [canDrag, canResize],
        );

        const finalLayout = useMemo(() => {
            return (
                layout?.map((current) => ({
                    ...baseLayout,
                    ...current,
                    i: current.i.toString(),
                })) || []
            );
        }, [baseLayout, layout]);

        const layoutProps = useMemo(() => {
            let rowHeight = adaptativeHeight ? gridHeight / 24 - MARGIN + 0.5 : ROW_HEIGHT;
            if (rowHeight < 16) rowHeight = 16;
            let baseProps = {
                layouts: layouts ? layouts : { lg: finalLayout },
                breakpoints: { lg: 1000, md: 796, sm: 568, xs: 280, xxs: 0 },
                cols: { lg: 24, md: 16, sm: 16, xs: 8, xxs: 8 },
                rowHeight: rowHeight,
                margin: [MARGIN, MARGIN],
                containerPadding: [0, 0],
                onLayoutChange,
                onResize,
                onResizeStop,
                onDrag,
                onDragStop,
                resizeHandles: ['s', 'w', 'e', 'n', 'sw', 'nw', 'se', 'ne'],
            };

            return baseProps;
        }, [
            layouts,
            finalLayout,
            adaptativeHeight,
            gridHeight,
            onLayoutChange,
            onResize,
            onResizeStop,
            onDrag,
            onDragStop,
        ]);

        const renderWidgets = useMemo(() => {
            if (!widgets?.length) return;
            return widgets.map((current) => {
                const isSelected = selected?.includes(current.id);

                return (
                    <div
                        key={current.id.toString()}
                        className={classnames(classes.gridItem, { [classes.selected]: isSelected })}
                    >
                        {current.content}
                    </div>
                );
            });
        }, [widgets, classes.gridItem, classes.selected, selected]);

        return (
            <div ref={gridRef} className={classes.root}>
                {gridHeight && (
                    <ResponsiveGridLayout {...layoutProps}>{renderWidgets}</ResponsiveGridLayout>
                )}
            </div>
        );
    },
);

export default GridLayout;
